import { useState } from "react";
import { ImplantDto, implantFormData, implantIdKey } from "../types/implant";
import { implantService } from "../services/implantService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useImplants = () => {
  // 1. GET
  const {
    data: implants = [],
    loading: isFetching,
    error: getError,
    refetch: fetchImplants,
    invalidateCache,
  } = useApi<ImplantDto[]>("implants", (signal) => implantService.getAll({}, signal));

  // 2. CREATE
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newImplant: typeof implantFormData) => implantService.create(newImplant));

  // 3. UPDATE
  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number | string; data: typeof implantFormData }) =>
    implantService.update(id, data)
  );

  // 4. DELETE
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => implantService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createImplant = async (newImplant: typeof implantFormData): Promise<void> => {
    try {
      await createExecution(newImplant);
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το εμφύτευμα αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      
      fetchImplants(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  // 💡 Η συνάρτηση UPDATE που έλειπε
  const updateImplant = async (id: number | string, updatedImplant: typeof implantFormData): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedImplant });
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το εμφύτευμα ενημερώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchImplants(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteImplants = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchImplants(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    implants,
    isLoading: isFetching || isCreating || isUpdating || isDeleting, // 💡 Προστέθηκε το isUpdating
    error: getError || createError || updateError || deleteError,      // 💡 Προστέθηκε το updateError
    fetchImplants: () => fetchImplants(true),
    createImplant,
    updateImplant,
    deleteImplants,
    modalState,
    closeModal,
  };
};